import _defineProperty from "@babel/runtime/helpers/defineProperty";
/** @jsx jsx */

import { createContext, useContext, useMemo } from 'react';
import { css, jsx } from '@emotion/core';
import { defaultMedium, spacingMapping, varColumnsNum, varColumnSpan } from './constants';
import { GridContext } from './grid-context'; // IE11 and Edge both have rounding issues for flexbox which is why a width of
// 99.9999% is used. Using 100% here causes columns to wrap prematurely.

var divisibleFullWidth = '99.9999%';
/**
 * Sets flex basis to occupy the specified number of columns.
 */

var getGridColumnSpanStyles = function getGridColumnSpanStyles(spacing) {
  var gap = "".concat(spacingMapping[spacing], "px");
  /**
   * We need to add the gap here to get the same results as before
   * because of how gaps were emulated with margins.
   *
   * Before the margins on the edges could extend past the edges
   * of the container, while still contributing to width calculations.
   */

  var availableWidth = "(".concat(divisibleFullWidth, " + ").concat(gap, ")");
  var singleColumnWidth = "".concat(availableWidth, " / var(").concat(varColumnsNum, ")");
  return css({
    minWidth: "calc(".concat(singleColumnWidth, " - ").concat(gap, ")"),
    maxWidth: "calc(".concat(singleColumnWidth, " *  var(").concat(varColumnSpan, ") - ").concat(gap, ")"),
    flexBasis: "100%"
  });
};

var gridColumnSpanStyles = {
  cosy: getGridColumnSpanStyles('cosy'),
  comfortable: getGridColumnSpanStyles('comfortable'),
  compact: getGridColumnSpanStyles('compact'),
  fullWidth: css({
    maxWidth: '100%',
    flexBasis: '100%'
  }),
  fillAvailable: css({
    maxWidth: '100%',
    flexBasis: 'auto'
  })
};
var gridColumnStyles = css({
  flexGrow: 1,
  flexShrink: 0,
  wordWrap: 'break-word'
});
/**
 * __Grid column context__
 *
 * @internal
 */

export var GridColumnContext = /*#__PURE__*/createContext({
  medium: defaultMedium
});
/**
 * __Grid column__
 *
 * A grid column can span one or more column positions within a grid.
 *
 * - [Examples](https://atlaskit.atlassian.com/packages/design-system/page)
 */

var GridColumn = function GridColumn(_ref) {
  var _ref$medium = _ref.medium,
      medium = _ref$medium === void 0 ? defaultMedium : _ref$medium,
      children = _ref.children,
      testId = _ref.testId;

  var _useContext = useContext(GridContext),
      spacing = _useContext.spacing,
      columns = _useContext.columns;

  var contextValue = useMemo(function () {
    return {
      medium: medium
    };
  }, [medium]);
  var colSpan = Math.max(1, Math.min(medium, columns));
  /**
   * The full-width case is handled separately because of rounding.
   */

  var isFullWidth = colSpan === columns;
  /**
   * A `medium` of 0 (the default) equates to an automatic flex-basis,
   * meaning the column will fill available space.
   */

  var shouldFillAvailable = medium === defaultMedium;
  return jsx(GridColumnContext.Provider, {
    value: contextValue
  }, jsx("div", {
    css: [gridColumnStyles, gridColumnSpanStyles[spacing], isFullWidth && gridColumnSpanStyles.fullWidth, shouldFillAvailable && gridColumnSpanStyles.fillAvailable],
    style: _defineProperty({}, varColumnSpan, shouldFillAvailable ? 'auto' : colSpan),
    "data-testid": testId
  }, children));
};

export default GridColumn;